'use client';

import { VStack } from '@terra/ui-styled-system';
import { automationId } from '@terra/shared-utils';
import { FileSearchIcon } from '@terra/ui-icons';
import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { useTranslations } from 'next-intl';
import { GuardrailCategorySchema } from '@terra/shared-schemas-orchestrator';
import { Button } from '../../components/button';
import {
  FilterBar,
  FilterBarEmptyState,
  type FilterConfig,
  type FilterValue,
} from '../../components/filter-bar';
import { Skeleton } from '../../components/skeleton';
import { ConfirmationDialog } from '../confirmation-dialog/ConfirmationDialog';
import { SettingsEmptyState } from '../settings/SettingsEmptyState';
import { FilterBarListScrollLayout } from '../list-page/FilterBarListScrollLayout';
import { toasterApi } from '../toast/ToastProvider';
import { toTestingControlsCopyScope } from '../testing-instructions/testing-controls-copy';
import { GuardrailCard, type GuardrailCardGuardrail } from './GuardrailCard';
import { GuardrailFormDialog } from './GuardrailFormDialog';
import {
  toGuardrailSubmitOutcome,
  type GuardrailFormValues,
  type GuardrailSubmitOutcome,
} from './guardrail-form.model';
import {
  ALL_GUARDRAIL_CATEGORIES,
  GUARDRAIL_CATEGORY_FILTER_ID,
  filterGuardrails,
  toggleExpandedId,
} from './guardrails-list.model';
import type { GuardrailsTarget } from './guardrails-target';

type GuardrailsPanelProps = {
  target: GuardrailsTarget;
  canManage: boolean;
  constrainListScroll?: boolean;
};

export type GuardrailsPanelHandle = {
  openCreateDialog: () => void;
};

export const GuardrailsPanel = forwardRef<
  GuardrailsPanelHandle,
  GuardrailsPanelProps
>(function GuardrailsPanel({ target, canManage, constrainListScroll }, ref) {
  const tPage = useTranslations('designSystem.testingControls');
  const t = useTranslations('designSystem.guardrails');
  const copyScope = toTestingControlsCopyScope(target.targetKind);
  const { guardrails, isLoading } = target.useGuardrails();
  const { saveAsync } = target.useSaveGuardrail();
  const { toggleAsync } = target.useToggleGuardrail();
  const { deleteAsync, isPending: isDeleting } = target.useDeleteGuardrail();
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>(
    ALL_GUARDRAIL_CATEGORIES,
  );
  const [expandedIds, setExpandedIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingGuardrail, setEditingGuardrail] =
    useState<GuardrailCardGuardrail | null>(null);
  const [pendingDelete, setPendingDelete] =
    useState<GuardrailCardGuardrail | null>(null);
  const hasNoGuardrails = guardrails.length === 0 && !isLoading;

  const filterConfigs: FilterConfig[] = useMemo(
    () => [
      {
        id: GUARDRAIL_CATEGORY_FILTER_ID,
        placeholder: tPage('categoryFilter'),
        items: GuardrailCategorySchema.options.map((category) => ({
          label: t(`categories.${category}`),
          value: category,
        })),
      },
    ],
    [t, tPage],
  );

  const filterBarValue: FilterValue = useMemo(
    () =>
      categoryFilter !== ALL_GUARDRAIL_CATEGORIES
        ? [{ id: GUARDRAIL_CATEGORY_FILTER_ID, value: [categoryFilter] }]
        : [],
    [categoryFilter],
  );

  const filteredGuardrails = useMemo(
    () => filterGuardrails({ guardrails, search, category: categoryFilter }),
    [guardrails, search, categoryFilter],
  );

  const handleOpenCreate = useCallback(() => {
    setEditingGuardrail(null);
    setDialogOpen(true);
  }, []);

  useImperativeHandle(ref, () => ({ openCreateDialog: handleOpenCreate }), [
    handleOpenCreate,
  ]);

  const handleToggleEnabled = async (guardrailId: string, enabled: boolean) => {
    try {
      await toggleAsync({ guardrailId, enabled });
      toasterApi.success({
        title: enabled ? t('success.enabled') : t('success.disabled'),
        description: enabled
          ? t('success.enabledDescription')
          : t('success.disabledDescription'),
      });
    } catch {
      toasterApi.error({
        title: t('errors.failedToUpdate'),
        description: t('errors.failedToUpdate'),
      });
    }
  };

  const handleSubmit = async (
    values: GuardrailFormValues,
  ): Promise<GuardrailSubmitOutcome> => {
    const response = await saveAsync({
      guardrailId: editingGuardrail?.id,
      values,
    });

    if (response.success) {
      target.invalidateGuardrails();
    }

    return toGuardrailSubmitOutcome(response);
  };

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    const guardrailId = pendingDelete.id;

    try {
      await deleteAsync(guardrailId);
      toasterApi.success({
        title: t('success.deleted'),
        description: t('success.deletedDescription'),
      });
      setExpandedIds((previous) =>
        previous.has(guardrailId)
          ? toggleExpandedId({ ids: previous, id: guardrailId })
          : previous,
      );
      setPendingDelete(null);
    } catch {
      toasterApi.error({
        title: t('errors.failedToDelete'),
        description: t('errors.failedToDelete'),
      });
    }
  };

  const filterBar = (
    <FilterBar
      filters={filterConfigs}
      value={filterBarValue}
      onFilterChange={(next) =>
        setCategoryFilter(
          next.find((entry) => entry.id === GUARDRAIL_CATEGORY_FILTER_ID)
            ?.value[0] ?? ALL_GUARDRAIL_CATEGORIES,
        )
      }
      onSearch={setSearch}
      searchPlaceholder={tPage('searchGuardrailsPlaceholder')}
      searchValue={search}
      px={0}
      pb={4}
      pt={4}
      showBottomBorder={false}
    />
  );

  const listContent = isLoading ? (
    <>
      <Skeleton h="122px" />
      <Skeleton h="122px" />
      <Skeleton h="122px" />
      <Skeleton h="122px" />
    </>
  ) : hasNoGuardrails ? (
    <SettingsEmptyState
      title={tPage('noPrefix', { prefix: tPage('tabs.guardrails') })}
      icon={<FileSearchIcon size={56} />}
      action={
        canManage ? (
          <Button
            size="sm"
            variant="solid"
            onClick={handleOpenCreate}
            {...(target.addGuardrailAutomationId
              ? automationId(target.addGuardrailAutomationId)
              : {})}
          >
            {tPage('addPrefix', { prefix: tPage('tabs.guardrails') })}
          </Button>
        ) : null
      }
    >
      {tPage(`emptyState.${copyScope}.noGuardrailsDescription`)}
    </SettingsEmptyState>
  ) : filteredGuardrails.length === 0 ? (
    <FilterBarEmptyState />
  ) : (
    filteredGuardrails.map((guardrail) => (
      <GuardrailCard
        key={guardrail.id}
        guardrail={guardrail}
        isExpanded={expandedIds.has(guardrail.id)}
        onToggleExpand={() =>
          setExpandedIds((previous) =>
            toggleExpandedId({ ids: previous, id: guardrail.id }),
          )
        }
        onToggleEnabled={(enabled) => {
          void handleToggleEnabled(guardrail.id, enabled);
        }}
        onEdit={() => {
          setEditingGuardrail(guardrail);
          setDialogOpen(true);
        }}
        onDelete={() => setPendingDelete(guardrail)}
        readOnly={!canManage}
        categoryBadgeLabel={
          guardrail.category === 'BUSINESS_LOGIC'
            ? tPage('badges.businessLogic')
            : undefined
        }
      />
    ))
  );

  const dialogs = (
    <>
      <GuardrailFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        guardrail={editingGuardrail}
        availablePipelines={target.availablePipelines}
        onSubmit={handleSubmit}
      />
      <ConfirmationDialog
        isOpen={pendingDelete !== null}
        loading={isDeleting}
        onOpenChange={({ open }) => {
          if (!open) setPendingDelete(null);
        }}
        onConfirm={() => {
          void handleConfirmDelete();
        }}
        onCancel={() => setPendingDelete(null)}
        variant="destructive"
        title={t('deleteGuardrail')}
        description={t('deleteConfirmation', {
          name: pendingDelete?.name ?? '',
        })}
        confirmText={t('deleteGuardrail')}
      />
    </>
  );

  if (constrainListScroll) {
    return (
      <>
        <FilterBarListScrollLayout filterBar={filterBar}>
          {listContent}
        </FilterBarListScrollLayout>
        {dialogs}
      </>
    );
  }

  return (
    <VStack alignItems="stretch" gap={4} w="full">
      {filterBar}
      {listContent}
      {dialogs}
    </VStack>
  );
});
